// src/pages/admin/Settings/SystemUsers.js
import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, Select, message, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import UserTable from '../UserTable'; // Updated path to point to src/pages/admin/UserTable.js

const { Option } = Select;
const { Title } = Typography;

const SystemUsers = ({ onLogout }) => { // Added onLogout prop
  const navigate = useNavigate();
  const [systemUsers, setSystemUsers] = useState([]);
  const [filteredSystemUsers, setFilteredSystemUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [form] = Form.useForm();
  const [sortColumn, setSortColumn] = useState(null); // For UserTable compatibility
  const [sortOrder, setSortOrder] = useState(null);  // For UserTable compatibility

  const initialSystemUsers = [
    { id: 1, name: 'Admin One', role: 'ADMIN', status: 'Active', email: 'admin1@jecthub.com', phone: '555-0101', createdAt: '2023-01-01' },
    { id: 2, name: 'Jane Accountant', role: 'ACCOUNTANT', status: 'Active', email: 'jane@jecthub.com', phone: '555-0102', createdAt: '2023-02-01' },
    { id: 3, name: 'Bob Support', role: 'SUPPORT', status: 'Active', email: 'bob@jecthub.com', phone: '555-0103', createdAt: '2023-03-01' },
    { id: 4, name: 'Alice HR', role: 'HR', status: 'Active', email: 'alice@jecthub.com', phone: '555-0104', createdAt: '2023-04-01' },
  ];

  const roleOptions = [
    { label: 'Admin', value: 'ADMIN' },
    { label: 'Accountant', value: 'ACCOUNTANT' },
    { label: 'Support', value: 'SUPPORT' },
    { label: 'HR', value: 'HR' },
    { label: 'Operations', value: 'OPERATIONS' },
  ];

  useEffect(() => {
    setLoading(true);
    // Simulate API fetch
    setTimeout(() => {
      setSystemUsers(initialSystemUsers);
      setFilteredSystemUsers(initialSystemUsers);
      setLoading(false);
    }, 500);

    // Placeholder for real API call
    /*
    fetch('/api/system-users')
      .then((response) => response.json())
      .then((data) => {
        setSystemUsers(data);
        setFilteredSystemUsers(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching system users:', error);
        setLoading(false);
      });
    */
  }, []);

  const handleSearch = (value) => {
    const filtered = systemUsers.filter(
      (user) =>
        user.name.toLowerCase().includes(value.toLowerCase()) ||
        user.email.toLowerCase().includes(value.toLowerCase()) ||
        user.role.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredSystemUsers(filtered);
    setCurrentPage(1);
  };

  const handleDeactivateUser = (userId) => {
    const user = systemUsers.find((u) => u.id === userId);
    const newStatus = user.status === 'Active' ? 'Inactive' : 'Active';
    Modal.confirm({
      title: `${newStatus === 'Inactive' ? 'Deactivate' : 'Activate'} User`,
      content: `Are you sure you want to ${newStatus.toLowerCase()} "${user.name}"?`,
      onOk: () => {
        const updatedUsers = systemUsers.map((u) => (u.id === userId ? { ...u, status: newStatus } : u));
        setSystemUsers(updatedUsers);
        setFilteredSystemUsers(updatedUsers);
        message.success(`User "${user.name}" ${newStatus.toLowerCase()}d successfully`);
      },
    });
  };

  const handleEditUser = (userId) => {
    const user = systemUsers.find((u) => u.id === userId);
    setSelectedUser(user);
    form.setFieldsValue(user);
    setIsEditModalVisible(true);
  };

  const handleAddUser = () => {
    setIsAddModalVisible(true);
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setIsViewModalVisible(true);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
    if (sorter.field && sorter.order) {
      const sorted = [...filteredSystemUsers].sort((a, b) => {
        const valueA = a[sorter.field].toString().toLowerCase();
        const valueB = b[sorter.field].toString().toLowerCase();
        return sorter.order === 'ascend' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      });
      setFilteredSystemUsers(sorted);
      setSortColumn(sorter.field);
      setSortOrder(sorter.order);
    } else {
      setSortColumn(null);
      setSortOrder(null);
    }
  };

  const handleAddUserSubmit = (values) => {
    const newUser = {
      id: systemUsers.length + 1,
      name: values.name,
      role: values.role,
      status: 'Active',
      email: values.email,
      phone: values.phone,
      createdAt: new Date().toISOString().split('T')[0],
    };
    const updatedUsers = [...systemUsers, newUser];
    setSystemUsers(updatedUsers);
    setFilteredSystemUsers(updatedUsers);
    setIsAddModalVisible(false);
    form.resetFields();
    message.success(`User "${newUser.name}" added successfully`);
  };

  const handleEditUserSubmit = (values) => {
    const updatedUsers = systemUsers.map((u) => (u.id === selectedUser.id ? { ...u, ...values } : u));
    setSystemUsers(updatedUsers);
    setFilteredSystemUsers(updatedUsers);
    setIsEditModalVisible(false);
    form.resetFields();
    message.success(`User "${values.name}" updated successfully`);
  };

  return (
    <div style={{ padding: '24px', background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
      <Title level={2} style={{ marginBottom: 16 }}>Manage System Users</Title>
      <UserTable
        users={filteredSystemUsers}
        loading={loading}
        currentPage={currentPage}
        pageSize={pageSize}
        handleSearch={handleSearch}
        handleViewUser={handleViewUser}
        handleEditUser={handleEditUser}
        handleDeactivateUser={handleDeactivateUser}
        handleAddUser={handleAddUser}
        handleTableChange={handleTableChange}
        sortColumn={sortColumn}
        sortOrder={sortOrder}
      />

      <Modal
        title="Add New System User"
        open={isAddModalVisible} // Changed from 'visible' for Ant Design v5 compatibility
        onCancel={() => {
          setIsAddModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleAddUserSubmit}>
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Please enter the user’s name' }]}>
            <Input placeholder="Enter user name" />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true, message: 'Please enter the email' }, { type: 'email', message: 'Please enter a valid email' }]}>
            <Input placeholder="Enter email" />
          </Form.Item>
          <Form.Item name="phone" label="Phone" rules={[{ required: true, message: 'Please enter the phone number' }]}>
            <Input placeholder="Enter phone number" />
          </Form.Item>
          <Form.Item name="role" label="Role" rules={[{ required: true, message: 'Please select a role' }]}>
            <Select placeholder="Select a role">
              {roleOptions.map((role) => (
                <Option key={role.value} value={role.value}>
                  {role.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">Add User</Button>
            <Button style={{ marginLeft: 8 }} onClick={() => { setIsAddModalVisible(false); form.resetFields(); }}>Cancel</Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`View User: ${selectedUser?.name || ''}`}
        open={isViewModalVisible} // Changed from 'visible'
        onOk={() => setIsViewModalVisible(false)}
        onCancel={() => setIsViewModalVisible(false)}
        okText="Close"
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        {selectedUser && (
          <div>
            <p><strong>Name:</strong> {selectedUser.name}</p>
            <p><strong>Email:</strong> {selectedUser.email}</p>
            <p><strong>Phone:</strong> {selectedUser.phone}</p>
            <p><strong>Role:</strong> {selectedUser.role}</p>
            <p><strong>Status:</strong> {selectedUser.status}</p>
            <p><strong>Created At:</strong> {selectedUser.createdAt}</p>
          </div>
        )}
      </Modal>

      <Modal
        title={`Edit User: ${selectedUser?.name || ''}`}
        open={isEditModalVisible} // Changed from 'visible'
        onCancel={() => {
          setIsEditModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleEditUserSubmit}>
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Please enter the user’s name' }]}>
            <Input placeholder="Enter user name" />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true, message: 'Please enter the email' }, { type: 'email', message: 'Please enter a valid email' }]}>
            <Input placeholder="Enter email" />
          </Form.Item>
          <Form.Item name="phone" label="Phone" rules={[{ required: true, message: 'Please enter the phone number' }]}>
            <Input placeholder="Enter phone number" />
          </Form.Item>
          <Form.Item name="role" label="Role" rules={[{ required: true, message: 'Please select a role' }]}>
            <Select placeholder="Select a role">
              {roleOptions.map((role) => (
                <Option key={role.value} value={role.value}>
                  {role.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">Save Changes</Button>
            <Button style={{ marginLeft: 8 }} onClick={() => { setIsEditModalVisible(false); form.resetFields(); }}>Cancel</Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SystemUsers;